import { useMutation, UseMutationOptions, UseMutationResult, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useClubStore } from '../store/api/useClubStore';
import { ClubLoginRequest, ClubLoginSaffRequest, ClubLoginMyClubRequest, ClubVerifyRequest, ClubRegisterRequest, ClubUpdateProfileRequest, ClubChangePasswordRequest, ClubUpdateRequest } from 'src/types/api/Club';
import { useAuthStore } from '../store/api/useAuthStore';

export const useClubLogin = (): UseMutationResult<any, AxiosError, ClubLoginRequest> => {
  const { loginClub } = useClubStore();

  const mutationOptions: UseMutationOptions<any, AxiosError, ClubLoginRequest> = {
    mutationFn: async (data: ClubLoginRequest) => {
      await loginClub(data);
      return { success: true };
    },
    onError: (error: AxiosError) => {
      console.error("Club login failed:", error.message);
    }
  };

  return useMutation<any, AxiosError, ClubLoginRequest>(mutationOptions);
};

export const useClubLoginSaff = (): UseMutationResult<any, AxiosError, ClubLoginSaffRequest> => {
  const { loginClubSaff } = useClubStore();

  const mutationOptions: UseMutationOptions<any, AxiosError, ClubLoginSaffRequest> = {
    mutationFn: async (data: ClubLoginSaffRequest) => {
      await loginClubSaff(data);
      return { success: true };
    },
    onError: (error: AxiosError) => {
      console.error("Club SAFF login failed:", error.message);
    }
  };

  return useMutation<any, AxiosError, ClubLoginSaffRequest>(mutationOptions);
};

export const useClubLoginMyClub = (): UseMutationResult<any, AxiosError, ClubLoginMyClubRequest> => {
  const { loginClubMyClub } = useClubStore();

  const mutationOptions: UseMutationOptions<any, AxiosError, ClubLoginMyClubRequest> = {
    mutationFn: async (data: ClubLoginMyClubRequest) => {
      await loginClubMyClub(data);
      return { success: true };
    },
    onError: (error: AxiosError) => {
      console.error("Club MyClub login failed:", error.message);
    }
  };

  return useMutation<any, AxiosError, ClubLoginMyClubRequest>(mutationOptions);
};

export const useClubVerify = (): UseMutationResult<any, AxiosError, ClubVerifyRequest> => {
  const { verifyClub } = useClubStore();

  const mutationOptions: UseMutationOptions<any, AxiosError, ClubVerifyRequest> = {
    mutationFn: async (data: ClubVerifyRequest) => {
      await verifyClub(data);
      return { success: true };
    },
    onError: (error: AxiosError) => {
      console.error("Club verification failed:", error.message);
    }
  };

  return useMutation<any, AxiosError, ClubVerifyRequest>(mutationOptions);
};

export const useClubRegister = (): UseMutationResult<any, AxiosError, ClubRegisterRequest> => {
  const { registerClub } = useClubStore();

  const mutationOptions: UseMutationOptions<any, AxiosError, ClubRegisterRequest> = {
    mutationFn: async (data: ClubRegisterRequest) => {
      await registerClub(data);
      return { success: true };
    },
    onError: (error: AxiosError) => {
      console.error("Club registration failed:", error.message);
    }
  };

  return useMutation<any, AxiosError, ClubRegisterRequest>(mutationOptions);
};

export const useClubProfileUpdate = (): UseMutationResult<any, AxiosError, ClubUpdateProfileRequest> => {
  const { updateClubProfile } = useClubStore();

  const mutationOptions: UseMutationOptions<any, AxiosError, ClubUpdateProfileRequest> = {
    mutationFn: async (data: ClubUpdateProfileRequest) => {
      await updateClubProfile(data);
      return { success: true };
    },
    onError: (error: AxiosError) => {
      console.error("Club profile update failed:", error.message);
    }
  };

  return useMutation<any, AxiosError, ClubUpdateProfileRequest>(mutationOptions);
};

export const useClubPasswordChange = (): UseMutationResult<any, AxiosError, ClubChangePasswordRequest> => {
  const { changeClubPassword } = useClubStore();

  const mutationOptions: UseMutationOptions<any, AxiosError, ClubChangePasswordRequest> = {
    mutationFn: async (data: ClubChangePasswordRequest) => {
      await changeClubPassword(data);
      return { success: true };
    },
    onError: (error: AxiosError) => {
      console.error("Club password change failed:", error.message);
    }
  };

  return useMutation<any, AxiosError, ClubChangePasswordRequest>(mutationOptions);
};

export const useClubUpdate = (): UseMutationResult<any, AxiosError, { clubId: string; data: ClubUpdateRequest }> => {
  const { updateClub } = useClubStore();

  const mutationOptions: UseMutationOptions<any, AxiosError, { clubId: string; data: ClubUpdateRequest }> = {
    mutationFn: async ({ clubId, data }) => {
      await updateClub(clubId, data);
      return { success: true };
    },
    onError: (error: AxiosError) => {
      console.error("Club update failed:", error.message);
    }
  };

  return useMutation<any, AxiosError, { clubId: string; data: ClubUpdateRequest }>(mutationOptions);
};

export const useClubDelete = (): UseMutationResult<any, AxiosError, string> => {
  const { deleteClub } = useClubStore();

  const mutationOptions: UseMutationOptions<any, AxiosError, string> = {
    mutationFn: async (clubId: string) => {
      await deleteClub(clubId);
      return { success: true };
    },
    onError: (error: AxiosError) => {
      console.error("Club delete failed:", error.message);
    }
  };

  return useMutation<any, AxiosError, string>(mutationOptions);
};

export const useClubBulkOperations = (): UseMutationResult<any, AxiosError, Array<{ action: string; clubId: string; data?: any }>> => {
  const { bulkOperations } = useClubStore();

  const mutationOptions: UseMutationOptions<any, AxiosError, Array<{ action: string; clubId: string; data?: any }>> = {
    mutationFn: async (operations: Array<{ action: string; clubId: string; data?: any }>) => {
      await bulkOperations(operations);
      return { success: true };
    },
    onError: (error: AxiosError) => {
      console.error("Club bulk operations failed:", error.message);
    }
  };

  return useMutation<any, AxiosError, Array<{ action: string; clubId: string; data?: any }>>(mutationOptions);
};

export const useClubLogout = () => {
  const { clearAll } = useClubStore();
  
  return () => {
    clearAll();
  };
};

export const useClubIsAuthenticated = () => {
  const { token } = useClubStore();
  
  return !!token;
};

export const useClubAuthState = () => {
  const { token } = useAuthStore();
  const clubState = useClubStore(state => ({
    tempToken: state.tempToken,
    permissions: state.permissions,
    clubNameEn: state.clubNameEn,
    clubNameAr: state.clubNameAr,
    personNameEn: state.personNameEn,
    personNameAr: state.personNameAr,
    mobile: state.mobile,
    email: state.email,
    username: state.username,
    clubId: state.clubId,
    idSystem: state.idSystem,
    avatar: state.avatar,
    isClub: state.isClub,
    isSaff: state.isSaff,
    imageGoogleAuthUrl: state.imageGoogleAuthUrl,
    isLoadingAuth: state.isLoadingAuth,
    loginStatus: state.loginStatus,
    loginMessage: state.loginMessage,
  }));

  return {
    token,
    ...clubState
  };
};

export const useClubDataState = () => {
  return useClubStore(state => ({
    token: state.token,
    permissions: state.permissions,
    clubNameEn: state.clubNameEn,
    clubNameAr: state.clubNameAr,
    personNameEn: state.personNameEn,
    personNameAr: state.personNameAr,
    mobile: state.mobile,
    email: state.email,
    username: state.username,
    clubId: state.clubId,
    idSystem: state.idSystem,
    avatar: state.avatar,
    isClub: state.isClub,
    isSaff: state.isSaff,
  }));
};

export const useClubRegistrationState = () => {
  return useClubStore(state => ({
    clubNameEnReg: state.clubNameEnReg,
    clubNameArReg: state.clubNameArReg,
    personNameEnReg: state.personNameEnReg,
    personNameArReg: state.personNameArReg,
    mobileReg: state.mobileReg,
    emailReg: state.emailReg,
    usernameReg: state.usernameReg,
    registrationError: state.registrationError,
    registrationHasAlert: state.registrationHasAlert,
    registrationStatus: state.registrationStatus,
    registrationMessage: state.registrationMessage,
    setClubNameEnReg: state.setClubNameEnReg,
    setClubNameArReg: state.setClubNameArReg,
    setPersonNameEnReg: state.setPersonNameEnReg,
    setPersonNameArReg: state.setPersonNameArReg,
    setMobileReg: state.setMobileReg,
    setEmailReg: state.setEmailReg,
    setUsernameReg: state.setUsernameReg,
    setRegistrationError: state.setRegistrationError,
    setRegistrationHasAlert: state.setRegistrationHasAlert,
    setRegistrationStatus: state.setRegistrationStatus,
    setRegistrationMessage: state.setRegistrationMessage,
    resetRegistrationForm: state.resetRegistrationForm,
    registerClub: state.registerClub,
  }));
};

export const useClubLoginModalState = () => {
  return useClubStore(state => ({
    loginMethod: state.loginMethod,
    loginClubId: state.loginClubId,
    loginPassword: state.loginPassword,
    saffCode: state.saffCode,
    myClubCode: state.myClubCode,
    googleAuthCode: state.googleAuthCode,
    loginStep: state.loginStep,
    captchaValue: state.captchaValue,
    generatedCaptcha: state.generatedCaptcha,
    isCaptchaValid: state.isCaptchaValid,
    hasAlert: state.hasAlert,
    error: state.error,
    successMessage: state.successMessage,
    isLoadingAuth: state.isLoadingAuth,
    isLoadingLogin: state.isLoadingLogin,
    isLoadingLoginSaff: state.isLoadingLoginSaff,
    isLoadingLoginMyClub: state.isLoadingLoginMyClub,
    isLoadingVerify: state.isLoadingVerify,
    loginError: state.loginError,
    loginSaffError: state.loginSaffError,
    loginMyClubError: state.loginMyClubError,
    verifyError: state.verifyError,
    loginStatus: state.loginStatus,
    loginMessage: state.loginMessage,
    setLoginMethod: state.setLoginMethod,
    setLoginClubId: state.setLoginClubId,
    setLoginPassword: state.setLoginPassword,
    setSaffCode: state.setSaffCode,
    setMyClubCode: state.setMyClubCode,
    setGoogleAuthCode: state.setGoogleAuthCode,
    setLoginStep: state.setLoginStep,
    setCaptchaValue: state.setCaptchaValue,
    setGeneratedCaptcha: state.setGeneratedCaptcha,
    setIsCaptchaValid: state.setIsCaptchaValid,
    setHasAlert: state.setHasAlert,
    setError: state.setError,
    setSuccessMessage: state.setSuccessMessage,
    resetLoginForm: state.resetLoginForm,
    loginClub: state.loginClub,
    loginClubSaff: state.loginClubSaff,
    loginClubMyClub: state.loginClubMyClub,
    verifyClub: state.verifyClub,
  }));
};

export const useClubManagementState = () => {
  return useClubStore(state => ({
    selectedTab: state.selectedTab,
    filterValue: state.filterValue,
    page: state.page,
    rowsPerPage: state.rowsPerPage,
    selectedClub: state.selectedClub,
    selectedClubs: state.selectedClubs,
    isEditModalOpen: state.isEditModalOpen,
    setSelectedTab: state.setSelectedTab,
    setFilterValue: state.setFilterValue,
    setPage: state.setPage,
    setRowsPerPage: state.setRowsPerPage,
    setSelectedClub: state.setSelectedClub,
    setSelectedClubs: state.setSelectedClubs,
    setIsEditModalOpen: state.setIsEditModalOpen,
    addSelectedClub: state.addSelectedClub,
    removeSelectedClub: state.removeSelectedClub,
    clearSelectedClubs: state.clearSelectedClubs,
  }));
};

export const useClubDataOptimized = (type: 'my-clubs' | 'saff-clubs' | 'test-clubs') => {
  const { token } = useClubAuthState();
  const { 
    fetchClubs, 
    fetchClubsSaff, 
    fetchClubsTest,
    clubs,
    clubsSaff,
    clubsTest,
    totalClubsClubs,
    totalClubsClubsSaff,
    totalClubsClubsTest
  } = useClubStore();
  
  const queryKey = ['clubs', type, token];
  
  return useQuery({
    queryKey,
    queryFn: async () => {
      console.log(`useClubDataOptimized: Fetching ${type} clubs`);
      try {
        switch (type) {
          case 'my-clubs':
            if (!token) {
              console.log('useClubDataOptimized: No token for my-clubs');
              return { clubs: [], totalClubs: 0 };
            }
            await fetchClubs();
            const currentClubs = useClubStore.getState().clubs;
            const currentTotalClubs = useClubStore.getState().totalClubsClubs;
            console.log('useClubDataOptimized: my-clubs result:', { clubs: currentClubs, totalClubs: currentTotalClubs });
            return { clubs: currentClubs || [], totalClubs: currentTotalClubs || 0 };
          case 'saff-clubs':
            if (!token) {
              console.log('useClubDataOptimized: No token for saff-clubs');
              return { clubs: [], totalClubs: 0 };
            }
            await fetchClubsSaff();
            const currentClubsSaff = useClubStore.getState().clubsSaff;
            const currentTotalClubsSaff = useClubStore.getState().totalClubsClubsSaff;
            console.log('useClubDataOptimized: saff-clubs result:', { clubs: currentClubsSaff, totalClubs: currentTotalClubsSaff });
            return { clubs: currentClubsSaff || [], totalClubs: currentTotalClubsSaff || 0 };
          case 'test-clubs':
            await fetchClubsTest();
            const currentClubsTest = useClubStore.getState().clubsTest;
            const currentTotalClubsTest = useClubStore.getState().totalClubsClubsTest;
            console.log('useClubDataOptimized: test-clubs result:', { clubs: currentClubsTest, totalClubs: currentTotalClubsTest });
            return { clubs: currentClubsTest || [], totalClubs: currentTotalClubsTest || 0 };
          default:
            console.log('useClubDataOptimized: Invalid type:', type);
            return { clubs: [], totalClubs: 0 };
        }
      } catch (error: any) {
        console.error('useClubDataOptimized: Error fetching clubs:', error);
        if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          return { clubs: [], totalClubs: 0 };
        }
        if (error.message?.includes('404') || error.message?.includes('Not Found')) {
          return { clubs: [], totalClubs: 0 };
        }
        throw error;
      }
    },
    enabled: !!token || type === 'test-clubs',
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount, error: any) => {
      if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        return false;
      }
      if (error.message?.includes('404') || error.message?.includes('Not Found')) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: 1000,
  });
};
